/**
 * Parse refine-agent interrupt `content` into a structured clarification payload.
 *
 * Backend may send valid JSON or a Python-repr dict string, e.g.:
 * {'message': '...', 'needs_clarification': [...], 'selected_entities': []}
 */

export interface ClarificationEntityMatch {
    column_name: string;
    entity_name: string;
}

export interface ClarificationTerm {
    entity_name: string;
    close_matches: ClarificationEntityMatch[];
}

export interface ClarificationPayload {
    message: string;
    needs_clarification: ClarificationTerm[];
    selected_entities: ClarificationEntityMatch[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function normalizeMatch(raw: unknown): ClarificationEntityMatch | null {
    if (!isRecord(raw)) return null;
    const column_name = raw.column_name;
    const entity_name = raw.entity_name;
    if (typeof column_name !== 'string' || typeof entity_name !== 'string') return null;
    if (!column_name.trim() || !entity_name.trim()) return null;
    return { column_name, entity_name };
}

function normalizeTerm(raw: unknown): ClarificationTerm | null {
    if (!isRecord(raw)) return null;
    const entity_name = raw.entity_name;
    if (typeof entity_name !== 'string' || !entity_name.trim()) return null;
    const closeRaw = raw.close_matches;
    const close_matches = Array.isArray(closeRaw)
        ? closeRaw.map(normalizeMatch).filter((m): m is ClarificationEntityMatch => m != null)
        : [];
    return { entity_name, close_matches };
}

function normalizePayload(parsed: unknown): ClarificationPayload | null {
    if (!isRecord(parsed)) return null;
    const needsRaw = parsed.needs_clarification;
    if (!Array.isArray(needsRaw) || needsRaw.length === 0) return null;

    const needs_clarification = needsRaw
        .map(normalizeTerm)
        .filter((t): t is ClarificationTerm => t != null);
    if (needs_clarification.length === 0) return null;

    const message =
        typeof parsed.message === 'string' && parsed.message.trim()
            ? parsed.message
            : `I couldn't find exact matches for ${needs_clarification.length} term${
                  needs_clarification.length === 1 ? '' : 's'
              }. Please clarify:`;

    const selectedRaw = parsed.selected_entities;
    const selected_entities = Array.isArray(selectedRaw)
        ? selectedRaw.map(normalizeMatch).filter((m): m is ClarificationEntityMatch => m != null)
        : [];

    return { message, needs_clarification, selected_entities };
}

/**
 * Convert a Python-style dict/list string into JSON-ish text.
 * Handles single-quoted keys and string values; leaves already-double-quoted
 * segments alone. Fragile for apostrophes inside single-quoted values, but
 * the clarify payloads typically use curly quotes (’ “ ”) in prose.
 */
function pythonReprToJson(raw: string): string {
    let out = '';
    let i = 0;
    while (i < raw.length) {
        const ch = raw[i];
        if (ch === '"' || ch === "'") {
            const quote = ch;
            let j = i + 1;
            let value = '';
            while (j < raw.length) {
                const c = raw[j];
                if (c === '\\' && j + 1 < raw.length) {
                    value += c + raw[j + 1];
                    j += 2;
                    continue;
                }
                if (c === quote) break;
                value += c;
                j += 1;
            }
            // Unclosed quote — append remainder as-is
            if (j >= raw.length) {
                out += raw.slice(i);
                break;
            }
            const escaped = value
                .replace(/\\/g, '\\\\')
                .replace(/"/g, '\\"')
                .replace(/\n/g, '\\n')
                .replace(/\r/g, '\\r')
                .replace(/\t/g, '\\t');
            out += `"${escaped}"`;
            i = j + 1;
            continue;
        }
        // Python literals
        if (raw.startsWith('None', i) && !/\w/.test(raw[i + 4] ?? '')) {
            out += 'null';
            i += 4;
            continue;
        }
        if (raw.startsWith('True', i) && !/\w/.test(raw[i + 4] ?? '')) {
            out += 'true';
            i += 4;
            continue;
        }
        if (raw.startsWith('False', i) && !/\w/.test(raw[i + 5] ?? '')) {
            out += 'false';
            i += 5;
            continue;
        }
        out += ch;
        i += 1;
    }
    return out;
}

function tryParseObject(raw: string): unknown | null {
    try {
        return JSON.parse(raw);
    } catch {
        // fall through
    }
    try {
        // Key-only rewrite (values may contain apostrophes)
        const keyFixed = raw.replace(/'([^']*)'\s*:/g, '"$1":');
        return JSON.parse(keyFixed);
    } catch {
        // fall through
    }
    try {
        return JSON.parse(pythonReprToJson(raw));
    } catch {
        return null;
    }
}

/**
 * Parse interrupt AI content into a clarification payload, or null if not clarification.
 */
export function parseClarificationContent(content: unknown): ClarificationPayload | null {
    if (content == null) return null;

    if (isRecord(content)) {
        return normalizePayload(content);
    }

    if (typeof content !== 'string') return null;
    const trimmed = content.trim();
    if (!trimmed) return null;

    // Already a plain human message (plan-review interrupt) — not clarification
    if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return null;

    const parsed = tryParseObject(trimmed);
    return normalizePayload(parsed);
}
